<template>
  <div v-if="shouldShow" class="coloring-legend">
    <!-- The WIDGET owns the scene, so its name is the heading; the coloured property is the
         subtitle. A property-only title is exactly what the legend contract forbids: two widgets
         coloured by Severity would caption their two very different scenes identically. -->
    <div class="coloring-legend__title" :title="title">{{ title }}</div>
    <div v-if="subtitle" class="coloring-legend__subtitle" :title="subtitle">{{ subtitle }}</div>
    <p v-if="coloringLegend.notice" class="coloring-legend__notice" role="status">{{ coloringLegend.notice }}</p>
    <!-- Colouring-source selector (D1): shown only when the current isolation spans more than one
         colouring source (charts coloured by different columns). Interactive island inside the
         otherwise pointer-events:none overlay. -->
    <select v-if="sources.length > 1" class="coloring-legend__source"
      :value="selectedKey" @change="onSourceChange($event)">
      <option v-for="s in sources" :key="s.key" :value="s.key">{{ s.label }}</option>
    </select>
    <!-- What the RECEIPT says about the scene as a whole: records, domain targets and geometry
         addresses are three different questions, so they stay three numbers and are never summed. -->
    <div v-if="countChips.length" class="coloring-legend__counts">
      <v-tooltip v-for="chip in countChips" :key="chip.key" bottom max-width="280">
        <template #activator="{ on, attrs }">
          <span class="coloring-legend__chip" :class="`is-${chip.key}`"
            tabindex="0" v-bind="attrs" v-on="on">{{ chip.value }} {{ chip.label }}</span>
        </template>
        <span>{{ chip.title }}</span>
      </v-tooltip>
    </div>
    <!-- Every partial/conflicting/windowed/mismatched/truncated outcome is stated. None of them may
         be silent: an invisible caveat is the same lie as a widened scope. -->
    <div v-if="stateBadges.length" class="coloring-legend__states">
      <v-tooltip v-for="badge in stateBadges" :key="badge.key" bottom max-width="280">
        <template #activator="{ on, attrs }">
          <span class="coloring-legend__badge" :class="`is-${badge.key}`"
            tabindex="0" v-bind="attrs" v-on="on">{{ badge.label }}</span>
        </template>
        <span>{{ badge.title }}</span>
      </v-tooltip>
    </div>
    <div class="coloring-legend__body">
      <div v-for="entry in entries" :key="entry.key || entry.value" class="coloring-legend__row"
        :class="rowClass(entry)"
        :role="isInteractive(entry) ? 'button' : null"
        :tabindex="isInteractive(entry) ? 0 : null"
        :aria-pressed="isInteractive(entry) ? String(entry.selected !== false) : null"
        @click="onEntryClick(entry)"
        @keydown.enter.prevent="onEntryClick(entry)"
        @keydown.space.prevent="onEntryClick(entry)">
        <span class="coloring-legend__swatch" :style="{ background: entry.color }"></span>
        <span class="coloring-legend__label" :title="entryLabel(entry)">{{ entryLabel(entry) }}</span>
        <v-tooltip v-if="countLabel(entry)" bottom max-width="280">
          <template #activator="{ on, attrs }">
            <span class="coloring-legend__count" tabindex="0" v-bind="attrs" v-on="on"
              @click.stop @keydown.enter.prevent.stop @keydown.space.prevent.stop>{{ countLabel(entry) }}</span>
          </template>
          <span>{{ countTitle(entry) }}</span>
        </v-tooltip>
      </div>
    </div>
    <!-- Restores the scene to the scope this isolate published. The dashboard's filter chips were
         never part of the refinement, so nothing about the board's data scope changes here. -->
    <button v-if="canReset" type="button" class="coloring-legend__reset" @click="onReset">
      Show all values
    </button>
  </div>
</template>

<script>
  import { compactNumber } from '@/features/Dashboard/utils/compactNumber';

  /** The conflict row the arbitrator owns. It is a STATE, not an encoding value. */
  const MIXED_ENTRY_KEY = '__mixed__';

  const plural = (count, noun) => (count === 1 ? noun.replace(/s$/, '') : noun);
  const capitalize = (value) => {
    const text = String(value || '');
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
  };

  export default {
    name: 'ColoringLegend',
    computed: {
      coloringLegend() {
        return this.$store.state.coloringLegend;
      },
      entries() {
        return this.coloringLegend?.entries || [];
      },
      /** Available colouring sources for the current isolation; a selector shows when >1. */
      sources() {
        return this.coloringLegend?.sources || [];
      },
      /** The currently-driving source key (the selector's value). */
      selectedKey() {
        return this.coloringLegend?.selectedKey || null;
      },
      /** PRIMARY: the widget/chart that owns the scene. `title`/`column` are the fallbacks a
       *  legend published without an owner still carries, so nothing ever renders headless. */
      title() {
        const legend = this.coloringLegend;
        return legend?.owner?.title || legend?.title || legend?.column || 'Colour Coding';
      },
      /** SECONDARY: which property supplied the colours. Never folded into the title. */
      subtitle() {
        const legend = this.coloringLegend;
        if (legend?.subtitle) return legend.subtitle;
        return legend?.property ? `Colored by ${legend.property}` : null;
      },
      /** The scene's own numbers, straight from the render receipt.
       *
       *  `addresses` is the SERVER's per-address total and is deliberately never added to anything:
       *  one address can carry fifty dbIds, so a sum would be a number describing nothing. A path
       *  that resolved no receipt publishes `counts: null` and shows no chips rather than zeroes it
       *  cannot vouch for. */
      countChips() {
        const counts = this.coloringLegend?.counts;
        if (!counts) return [];
        const chips = [];
        const add = (key, label, value, title) => {
          if (!Number.isFinite(value)) return;
          chips.push({ key, label: plural(value, label), value, title });
        };
        add('records', 'Records', counts.records, 'Records included in this 3D selection.');
        if (Number.isFinite(counts.targets)) {
          chips.push({ key: 'targets', value: counts.targets,
            label: `${capitalize(plural(counts.targets, this.coloringLegend.targetLabel || 'linked items'))} shown`,
            title: 'Linked equipment or record locations displayed in 3D.' });
        }
        if (Number.isFinite(counts.unresolved) && counts.unresolved > 0) {
          chips.push({ key: 'unresolved', value: counts.unresolved, label: 'Not shown',
            title: 'Records whose location could not be displayed in 3D.' });
        }
        return chips;
      },
      /** Every outcome the receipt reports that is NOT simply "it rendered". None may be silent. */
      stateBadges() {
        const legend = this.coloringLegend;
        if (!legend) return [];
        const badges = [];
        const window = legend.window;
        if (window
          && Number.isFinite(window.recordsInWindow)
          && Number.isFinite(window.totalFilteredBeforeWindow)
          && window.recordsInWindow < window.totalFilteredBeforeWindow) {
          badges.push({
            key: 'window',
            label: `Showing ${window.recordsInWindow} of ${window.totalFilteredBeforeWindow} filtered records`,
            title: 'This widget displays a ranked slice, and the scene covers exactly that slice',
          });
        }
        if (legend.conflicts > 0) {
          badges.push({
            key: 'mixed',
            label: `Shared locations: ${legend.conflicts}`,
            title: 'Several selected categories refer to the same location. A shared colour is used.',
          });
        }
        if (legend.modelMismatches > 0) {
          badges.push({
            key: 'modelMismatch',
            label: `Other model: ${legend.modelMismatches}`,
            title: 'These locations belong to another model and are not displayed here.',
          });
        }
        if (legend.partial === true) {
          badges.push({ key: 'partial', label: 'Some locations are not shown', title: 'Some selected records could not be displayed in 3D.' });
        }
        if (legend.truncated === true) {
          badges.push({ key: 'truncated', label: 'Only part of the selection was checked', title: 'This selection is too large to display completely. Narrow the filters to see a smaller set.' });
        }
        return badges;
      },
      /** The dimensions this legend describes: today the coloured one alone, and only a chart
       *  isolate publishes them. A legend without facets (the datagrid's) renders exactly as it
       *  always has — read-only rows, no reset. */
      facets() {
        return this.coloringLegend?.facets || [];
      },
      /** Whether rows may be clicked to narrow the scene. The publisher decides: a truncated
       *  receipt describes only the first N rows, so narrowing it would claim a completeness the
       *  server already said it could not give, and that legend stays a read-only receipt. */
      canRefine() {
        return this.facets.length > 0 && this.coloringLegend?.refinable === true;
      },
      canReset() {
        return this.canRefine && this.coloringLegend?.refined === true;
      },
      shouldShow() {
        // View-scoping (3D-only) is handled by the parent slot's `activeTab === '3D View'`
        // guard; here we only need at least one value to render. De-isolate / close clears
        // the store field, which empties entries and hides the overlay.
        return this.entries.length > 0 || !!this.coloringLegend?.notice || !!this.coloringLegend?.counts;
      },
    },
    methods: {
      /** Emit the chosen colouring-source key; the parent re-isolates coloured by it. */
      onSourceChange(e) {
        this.$emit('select-source', e.target.value);
      },
      entryLabel(entry) {
        return entry.label != null ? entry.label : entry.value;
      },
      /** A row is clickable only when this legend refines, the row has something on screen, and the
       *  row is an encoding VALUE. MIXED is a conflict between styles rather than one of them, so
       *  "refine to MIXED" is not a scope a user can ask for. */
      isInteractive(entry) {
        if (!this.canRefine) return false;
        if (entry.key === MIXED_ENTRY_KEY || entry.state === 'mixed') return false;
        return entry.paintable !== false && entry.targets !== 0;
      },
      /** Muted = "not on screen right now", whether because the row resolved no geometry or because
       *  a refinement is currently hiding it. One visual language for one meaning. */
      rowClass(entry) {
        return {
          'is-clickable': this.isInteractive(entry),
          'is-muted': entry.paintable === false || entry.targets === 0 || entry.selected === false,
        };
      },
      onEntryClick(entry) {
        if (!this.isInteractive(entry)) return;
        this.$emit('refine-value', entry.value);
      },
      onReset() {
        this.$emit('reset-refinement');
      },
      /**
       * "<records> (<targets> targets)" — how many exact source ROWS this value covers, then how
       * many distinct domain subjects of it actually reached the screen. The two are different
       * questions and one row can own many elements, so reporting only geometry made the legend
       * read as if it contradicted the grid it captions.
       *
       * `dataCount` (the number the CHART showed) is quoted only when it DISAGREES with the
       * receipt. On an exact scope the two agree by construction — invariant 2 — so repeating it
       * would be noise; a disagreement is the interesting case and must stay visible.
       * A target count the receipt could not attribute (two values sharing one colour) is omitted
       * rather than guessed.
       */
      countLabel(entry) {
        const records = Number.isFinite(entry.records) ? entry.records : null;
        const head = records === null
          ? ''
          : (Number.isFinite(entry.dataCount) && entry.dataCount !== records
            ? `${records} of ${compactNumber(entry.dataCount)}`
            : `${records}`);
        if (!Number.isFinite(entry.targets)) return head;
        const tail = `${entry.targets} Shown`;
        return head ? `${head} ${plural(records, 'Records')} · ${tail}` : tail;
      },
      countTitle(entry) {
        const hasRecords = Number.isFinite(entry.records);
        const hasTargets = Number.isFinite(entry.targets);
        if (hasRecords && hasTargets) return 'Records in this category and linked items displayed for them in 3D.';
        if (hasRecords) return 'Records included in this category.';
        return 'Linked items displayed for this category in 3D.';
      },
    },
  };
</script>

<style scoped>
  .coloring-legend__notice { white-space: normal; line-height: 1.5; margin: 8px 0; }
  .coloring-legend {
    position: absolute;
    left: 16px;
    top: 16px;
    z-index: 10;
    padding: 10px 12px 10px 12px;
    color: #ffffff;
    /* Sized to its widest ROW (label + counts) rather than a fixed box: the dual counts
       ("3 (2 tags)") must never be clipped or pushed under the scrollbar. Only an absurdly long
       value name hits the max and ellipsises — the counts never shrink. */
    width: max-content;
    min-width: 160px;
    max-width: 320px;
    pointer-events: none;
    font-size: 12px;
    line-height: 1.2;
    user-select: none;
    background: rgba(10, 14, 20, 0.78);
    border-radius: 6px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
  }

  .coloring-legend__title {
    font-weight: 600;
    font-size: 11px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    opacity: 0.82;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Secondary by every visual means available: lighter, smaller, sentence case. The owner has to
     win the glance, because two widgets coloured by one property caption two different scenes. */
  .coloring-legend__subtitle {
    font-weight: 400;
    font-size: 10px;
    opacity: 0.62;
    margin-top: 2px;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .coloring-legend__counts,
  .coloring-legend__states {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 4px;
    margin-top: 8px;
  }

  .coloring-legend__chip {
    padding: 2px 5px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    white-space: nowrap;
    background: rgba(255, 255, 255, 0.04);
    pointer-events: auto;
    cursor: help;
  }

  /* Unresolved is the one count that reports an absence, so it is the one that must not blend in. */
  .coloring-legend__chip.is-unresolved {
    background: transparent;
    border-style: dashed;
    opacity: 0.78;
  }

  .coloring-legend__badge {
    padding: 2px 5px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.02em;
    white-space: nowrap;
    background: rgba(255, 255, 255, 0.05);
    pointer-events: auto;
    cursor: help;
  }

  /* MIXED reads with the same non-severity token the arbitrator paints it with, so the badge, the
     swatch and the element on screen are one colour rather than three. */
  .coloring-legend__badge.is-mixed {
    border-color: var(--ds-color-mixed, #665b9b);
    background: rgba(102, 91, 155, 0.22);
  }

  .coloring-legend__states {
    margin-bottom: 2px;
  }

  .coloring-legend__counts + .coloring-legend__states {
    margin-top: 4px;
  }

  .coloring-legend__counts,
  .coloring-legend__states,
  .coloring-legend__title,
  .coloring-legend__subtitle {
    margin-bottom: 0;
  }

  .coloring-legend__body {
    margin-top: 8px;
  }

  /* Interactive island inside the pointer-events:none overlay: re-enable input so the source
     selector is clickable. */
  .coloring-legend__source {
    pointer-events: auto;
    width: 100%;
    margin-bottom: 8px;
    padding: 4px 6px;
    font-size: 11px;
    color: #ffffff;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 4px;
    cursor: pointer;
  }

  .coloring-legend__source option {
    color: #111111;
  }

  /* The ONLY scroll area: the title, the source selector and the reset control are siblings, so
     they stay pinned while the values scroll. The panel grows to fit up to --legend-visible-rows
     before scrolling at all, so a short legend has no scrollbar and no dead space.
     overflow-x is hidden outright — with overflow-y set, a `visible` x-axis computes to `auto` and
     a single pixel of row overhang would raise a horizontal scrollbar under the rows.
     scrollbar-gutter reserves the thin bar's track so a count can never sit under the thumb, and
     the panel does not resize the moment it becomes scrollable. */
  .coloring-legend__body {
    --legend-row-height: 18px;
    --legend-row-gap: 4px;
    --legend-visible-rows: 8;
    display: flex;
    flex-direction: column;
    gap: var(--legend-row-gap);
    max-height: calc(
      var(--legend-visible-rows) * var(--legend-row-height)
      + (var(--legend-visible-rows) - 1) * var(--legend-row-gap)
    );
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-gutter: stable;
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.28) transparent;
  }

  /* Thin overlay-styled scrollbar (the ScanSelectorPanel pattern, re-toned for this dark overlay).
     The stepper buttons are removed explicitly: the native Windows arrows are what clipped the
     counts. */
  .coloring-legend__body::-webkit-scrollbar {
    width: 6px;
  }

  .coloring-legend__body::-webkit-scrollbar-track {
    background: transparent;
  }

  .coloring-legend__body::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.28);
    border-radius: 3px;
  }

  .coloring-legend__body::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.45);
  }

  .coloring-legend__body::-webkit-scrollbar-button {
    display: none;
  }

  .coloring-legend__row {
    display: flex;
    align-items: center;
    gap: 8px;
    box-sizing: border-box;
    min-height: var(--legend-row-height);
    padding: 6px;
  }

  /* Interactive island inside the pointer-events:none overlay (same exemption the source selector
     takes): without this the rows render perfectly and swallow every click. The hover surface is
     the row's own padding — never a negative margin, which would push the row past the scroll
     container's content box and raise the horizontal scrollbar again. */
  .coloring-legend__row.is-clickable {
    pointer-events: auto;
    cursor: pointer;
    border-radius: 3px;
  }

  .coloring-legend__row.is-clickable:hover,
  .coloring-legend__row.is-clickable:focus-visible {
    background: rgba(255, 255, 255, 0.12);
    outline: none;
  }

  /* Not painting right now: a value the isolate resolved no geometry for, or one a refinement is
     hiding. Dimming the whole row carries the swatch with it. */
  .coloring-legend__row.is-muted {
    opacity: 0.45;
  }

  .coloring-legend__swatch {
    width: 14px;
    height: 14px;
    flex: 0 0 14px;
    border-radius: 3px;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.22);
  }

  /* The only element allowed to give way: min-width 0 lets a very long value name ellipsise once
     the panel hits its max width, instead of pushing the counts out of the row. */
  .coloring-legend__label {
    flex: 1 1 auto;
    min-width: 0;
    font-weight: 500;
    font-size: 11px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .coloring-legend__count {
    flex: 0 0 auto;
    font-size: 11px;
    font-weight: 600;
    opacity: 0.85;
    margin-left: auto;
    white-space: nowrap;
    pointer-events: auto;
    cursor: help;
  }

  /* min-width: max-content puts the control's own label into the panel's intrinsic width, so the
     panel fits "Show all values" as well as its widest row — the button never wraps or clips. */
  .coloring-legend__reset {
    pointer-events: auto;
    width: 100%;
    min-width: max-content;
    white-space: nowrap;
    margin-top: 8px;
    padding: 4px 6px;
    font-size: 11px;
    font-weight: 600;
    color: #ffffff;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 4px;
    cursor: pointer;
  }

  .coloring-legend__reset:hover {
    background: rgba(255, 255, 255, 0.16);
  }
</style>
